import { z } from 'zod';
import { createModel } from '../../common';

/**
 * Every app that appears in the Apps & Integrations marketplace.
 *
 * The enum covers the whole catalog, including entries with no connector yet,
 * so a card and its future `AppInstalled` row share one key. Whether Hero can
 * `AppDefinition.status` in `@hero/apps` (soon moving fully to the `App`
 * collection) drives marketplace lifecycle — connect vs request access vs hidden.
 *
 * `logisti` is ELM's Bayan platform, marketed in Saudi Arabia as Logisti.
 */
export const appIdSchema = z.enum([
  'logisti',
  'alrajhi-bank',
  'snb',
  'alinma',
  'whatsapp',
  'daftra',
  'qoyod',
  'sap',
  'oracle',
  'custom-app',
]);
export type AppId = z.infer<typeof appIdSchema>;

/** Marketplace grouping, used for the category filter. */
export const appCategorySchema = z.enum([
  'banks',
  'accounting',
  'erp',
  'government',
  'messaging',
  'custom',
]);
export type AppCategory = z.infer<typeof appCategorySchema>;

/**
 * Marketplace lifecycle for one app:
 * - `active`   — connector live; Connect / Manage is offered.
 * - `beta`     — connector live under beta; same affordances as `active`.
 * - `soon`     — card visible; Request early access only.
 * - `inactive` — hidden from the catalog (existing installs keep working).
 */
export const appStatusSchema = z.enum(['active', 'beta', 'soon', 'inactive']);
export type AppStatus = z.infer<typeof appStatusSchema>;

/**
 * Optional promotional badge on a marketplace card or connect modal.
 *
 * Separate from {@link AppStatus}: lifecycle drives behaviour; `new` /
 * `soon` / `beta` / `popular` are optional marketing labels on top.
 */
export const appPromoLabelSchema = z.enum(['new', 'soon', 'beta', 'popular']);
export type AppPromoLabel = z.infer<typeof appPromoLabelSchema>;

/**
 * Domain events an app can react to.
 *
 * Declared so the catalog can tell a user what connecting an app will
 * actually do. Nothing dispatches off this list yet — the Logisti connector
 * still subscribes to order lifecycle hooks directly.
 */
export const appEventSchema = z.enum([
  'order_created',
  'order_driver_assigned',
  'order_first_pickup_done',
  'order_delivered',
  'order_canceled',
  'invoice_issued',
  'payment_captured',
]);
export type AppEvent = z.infer<typeof appEventSchema>;

/**
 * Health of one Org's install, refreshed on connect and after every call.
 */
export const appHealthSchema = z.enum([
  /** Credentials verified, last call succeeded. */
  'healthy',
  /** Credentials present but the last call failed (provider down, license expired). */
  'degraded',
  /** Credentials rejected — the Org must re-enter them. */
  'unauthorized',
  /** Installed but never exercised yet. */
  'unknown',
]);
export type AppHealth = z.infer<typeof appHealthSchema>;

/**
 * `App db model` — per-deployment overrides for a marketplace entry.
 *
 * ## Why this holds so little
 *
 * The catalog itself lives in code, in `@hero/apps` — one folder per app
 * carrying its definition next to its logic, so a card can never advertise an
 * app whose connector was deleted. What genuinely belongs in the database is
 * the small set of things an operator should be able to change without a
 * release: hiding an app, or flipping a live one back to `soon` / `inactive`
 * while a provider outage is investigated.
 *
 * A row is therefore optional. An app with no row behaves exactly as its
 * `AppDefinition` says.
 */
export const AppSchema = createModel({
  appId: appIdSchema,

  /** Hide the card entirely. Existing installs keep working. */
  hidden: z.boolean().default(false),

  /** Overrides the code catalog's status, e.g. to park a live app during an outage. */
  status: appStatusSchema.optional(),

  /** Why the override is in place, shown to Hero staff in the admin panel. */
  note: z.string().optional(),
});

export type App = z.infer<typeof AppSchema.model>;
export type AppData = z.infer<typeof AppSchema.data>;
export type AppPartial = z.infer<typeof AppSchema.partial>;
